<template>
  <auth-layout :page="'signup'">
    <h1 class="text-xl mb-2 text-bold">Watch this first:</h1>

    <a
      href="https://www.loom.com/share/c3da89f3e6994aebb9652ab1f1666d30"
      target="lsa-help-video"
      style="font-size: 12px;"
    >
      <i class="mdi mdi-information-outline" style="font-size: 14px;"></i>
      How to get started on our software
    </a>

    <h1 class="text-xl mb-2 text-bold mt-4">Sign up for LSA Command</h1>

    <p v-show="e1 != 3" class="text-xs mb-4">
      Enter your information below to create your account.
    </p>

    <div>
      <div
        v-if="success"
        class="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4"
        role="alert"
      >
        {{ success }}
      </div>

      <div
        v-if="error"
        class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
        role="alert"
      >
        <div v-html="error"></div>
      </div>

      <v-stepper v-model="e1">
        <v-stepper-header style="display: none;">
          <v-stepper-step :complete="e1 > 1" step="1">
            Account Setup
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="e1 > 2" step="2">
            Company Information
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <!-- Products -->
            <div class="mb-6">
              <p class="mb-2 text-xs font-bold">Select Products</p>

              <div class="border rounded p-3 pb-1 mb-3">
                <label class="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="lsa_command_enabled"
                    class="mt-1 mr-3"
                  />
                  <div>
                    <div class="font-bold text-sm">LSA Command — $100/month</div>
                    <div class="text-xs text-gray-600 mt-1">
                      Add your LSA CID, connect your LSA account, pull LSA stats and leads,
                      create reports, and submit LSA surveys.
                    </div>
                  </div>
                </label>
              </div>

              <div class="border rounded p-3 pb-1 mb-3">
                <label class="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="convertly_enabled"
                    class="mt-1 mr-3"
                  />
                  <div>
                    <div class="font-bold text-sm">Convertly — $200/month</div>
                    <div class="text-xs text-gray-600 mt-1">
                      AI Agent for calls, SMS messages, and web chat widget conversations.
                      <br />
                      <strong>200</strong> conversations/month included, <strong>$1.00</strong> per conversation after that.
                    </div>
                  </div>
                </label>
              </div>

              <div class="bg-gray-100 rounded p-3 text-sm">
                <strong>Total:</strong> ${{ monthlyTotal }}/month
              </div>

              <div class="bg-gray-100 rounded p-3 mt-1 text-sm">
                <strong>Free Trial:</strong> 7 days, $0.00 charged today
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">First Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-first-name"
                    type="text"
                    ref="first_name"
                    placeholder="e.g. John"
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Last Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-last-name"
                    type="text"
                    ref="last_name"
                    placeholder="e.g. Doe"
                  />
                </div>
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Company Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-city"
                    type="text"
                    ref="company_name"
                    v-model="company_name"
                    placeholder="e.g. My Company Inc."
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Phone Number</p>
                  <input
                    type="text"
                    ref="phone"
                    v-model="phone"
                    @keyup="phone_valid = false"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    placeholder="e.g. +18123124214"
                  />
                </div>
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Email</p>
                  <input
                    type="email"
                    ref="email"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    placeholder="e.g. johndoe@example.com"
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Password</p>
                  <input
                    type="password"
                    ref="password"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    autocomplete="new-password"
                    placeholder="Create Password"
                  />
                </div>
              </div>
            </div>

            <div class="-mx-3" v-if="lsa_command_enabled">
              <div class="flex flex-wrap mb-2">
                <div class="w-full px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">
                    Google LSA CID (for survey/refund process)
                  </p>
                  <input
                    type="text"
                    ref="lsa_cid"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    placeholder="Enter your Google LSA Account CID"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                class="w-full rounded bg-purple text-white text-bold text-xs focus:outline-none py-3 px-4"
                :class="{ spinner: registering }"
                @click="submit()"
              >
                CREATE ACCOUNT
              </button>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2">
            <div
              v-show="company_type == 'business'"
              :class="'w-full ' + ((only_business) ? '' : '') + ' pl-3 mb-3 md:mb-0 -mx-3'"
            >
              <p class="text-xs text-gray-700 mt-2">
                Use our tool below to search for your business online, or enter your details manually.
              </p>

              <v-combobox
                label="Search for your business"
                v-model="autocompleteLocationModel"
                :items="locationFoundItems"
                :search-input.sync="locationSearchText"
                item-text="value"
                item-value="id"
                hide-no-data
                return-object
                :filter="filterPlaces"
                color="grey"
                background-color="grey lighten-4"
                @change="selectPlace()"
                dense
                outlined
              >
              </v-combobox>
            </div>

            <div class="flex flex-wrap -mx-3 -mt-4">
              <div :class="'w-full ' + ((only_business) ? 'md:w-1/2 px-3' : 'pl-3') + ' mb-4 md:mb-0'">
                <br v-if="company_type != 'business'" />
                <p class="mb-1 text-xs mb-2">Company Name</p>
                <input
                  class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                  id="grid-city"
                  type="text"
                  ref="company_name"
                  v-model="company_name"
                  placeholder="e.g. My Company Inc."
                />
              </div>

              <div v-if="only_business" :class="'w-full md:w-1/2 pl-3 mb-4 md:mb-0'">
                <p class="mb-1 text-xs mb-2">Country</p>

                <v-select
                  :items="countries"
                  label=""
                  item-text="name"
                  item-value="code"
                  hide-details
                  outlined
                  dense
                  v-model="business_location.country"
                  class="-mt-2"
                  color="grey"
                ></v-select>
              </div>
            </div>

            <div class="flex flex-wrap -mx-3">
              <div v-if="only_business" :class="'w-full md:w-1/2 px-3 mb-4 md:mb-0'">
                <p class="mb-1 text-xs mb-2">Address</p>
                <input
                  class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                  type="text"
                  v-model="business_location.address"
                  placeholder="e.g. 5th Avenue"
                />
              </div>
              <div v-if="only_business" :class="'w-full md:w-1/2 pl-3 mb-4 md:mb-0'">
                <p class="mb-1 text-xs mb-2">City</p>
                <input
                  class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                  type="text"
                  v-model="business_location.city"
                  placeholder="e.g. New York"
                />
              </div>
            </div>

            <div class="flex flex-wrap -mx-3">
              <div v-if="only_business" :class="'w-full md:w-1/2 pl-3 mb-4 md:mb-0'">
                <p class="mb-1 text-xs mb-2">
                  <span v-if="business_location.country == 'US'">State</span>
                  <span v-else>County</span>
                </p>
                <input
                  class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                  type="text"
                  v-model="business_location.state"
                  placeholder="e.g. NY"
                />
              </div>

              <div v-if="only_business" :class="'w-full md:w-1/2 pl-3 mb-4 md:mb-0'">
                <p class="mb-1 text-xs mb-2">
                  <span v-if="business_location.country == 'US'">Zip</span>
                  <span v-else>Postal Code</span>
                </p>
                <input
                  class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                  type="text"
                  v-model="business_location.zip"
                  placeholder="00001"
                />
              </div>
            </div>

            <div class="pt-3 pr-3">
              <button
                class="w-full rounded bg-purple text-white text-bold text-xs focus:outline-none py-3 px-4"
                :class="{ spinner: registering }"
                @click="submit()"
              >
                CREATE ACCOUNT
              </button>

              <button
                class="w-full rounded border border-blue bg-white text-primary text-bold text-xs focus:outline-none py-3 px-4 mt-3"
                @click="e1 = 1"
              >
                BACK
              </button>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <div v-show="e1 == 1" class="mt-6 text-xs">
        <div class="float-right">
          Have an agency? 

          <router-link class="no-underline align-baseline font-medium text-xs" to="/signup-agency">
            Signup for White Label
          </router-link>
        </div>
        Already have an account?
        <router-link class="no-underline align-baseline font-medium text-xs" to="/login">
          Login
        </router-link>
      </div>
    </div>
  </auth-layout>
</template>

<script>
import { mapState } from 'vuex'
import AuthLayout from '@/components/AuthLayout.vue'
import { GetSuggestions } from '@/utils/PlaceUtils'
import $ from 'jquery'
import axios from 'axios'
import countries from '@/config/countries'

export default {
  components: {
    AuthLayout
  },

  data: function() {
    return {
      company_name: '',

      success: null,
      info: null,
      error: null,
      registering: false,
      accountCreated: false,
      authenticating: false,

      company_type: 'business',
      only_business: true,

      autocompleteLocationModel: null,
      locationSearchText: null,
      locationEntries: [],

      pull_google_reviews: false,

      business_location: {
        country: 'US'
      },

      countries: countries,

      e1: 1,

      phone: '',
      phone_valid: false,

      agree: false,

      // New product flags
      lsa_command_enabled: true,
      convertly_enabled: false
    }
  },

  computed: {
    ...mapState({
      me: state => state.me
    }),

    locationFoundItems() {
      return this.locationEntries
    },

    monthlyTotal() {
      let total = 0

      if (this.lsa_command_enabled) total += 100
      if (this.convertly_enabled) total += 200

      return total
    }
  },

  metaInfo() {
    return {
      title: 'Sign Up | ' + this.$appName
    }
  },

  methods: {
    async submit() {
      this.error = null

      if (!this.lsa_command_enabled && !this.convertly_enabled) {
        this.error = 'Please select at least one product.'
        return
      }

      this.registering = true

      try {
        const params = {
          company_name: this.company_name.trim() || this.$refs.company_name.value.trim(),
          company_type: 'business',
          first_name: this.$refs.first_name.value.trim(),
          last_name: this.$refs.last_name.value.trim(),
          email: this.$refs.email.value.trim(),
          phone: this.$refs.phone.value.trim(),
          password: this.$refs.password.value.trim(),
          pull_google_reviews: this.pull_google_reviews,
          lsa_cid: this.lsa_command_enabled ? this.$refs.lsa_cid.value.trim() : null,
          business_location: this.business_location,

          // New fields for backend
          lsa_command_enabled: this.lsa_command_enabled,
          convertly_enabled: this.convertly_enabled
        }

        if (window.location.origin != process.env.VUE_APP_MAIN_URL) {
          params.host = window.location.host
        }

        if (this.$cookie.get('RGEmbedWidgetToken')) {
          params.RGEmbedWidgetToken = this.$cookie.get('RGEmbedWidgetToken')
        }

        if (this.$cookie.get('RGEmbedPlatformURL')) {
          params.RGEmbedPlatformURL = this.$cookie.get('RGEmbedPlatformURL')
        }

        if (this.$route.query.u) {
          params.u = this.$route.query.u
        }

        await this.$store.dispatch('auth/signup', params)

        setTimeout(() => {
          if (this.$cookie.get('RGEmbedWidgetToken')) {
            window.top.location = '/widgets/edit/' + this.$cookie.get('RGEmbedWidgetToken')
          } else {
            window.location = '/dashboard?signup=1'
          }
        }, 1000)
      } catch (error) {
        for (var i in error.errors) {
          if (error.errors[i] != 'null') {
            this.error = (this.error || '') + error.errors[i] + '<br />'
          }
        }
      }

      this.registering = false
    },

    getCookie(cname) {
      var name = cname + '='
      var decodedCookie = decodeURIComponent(document.cookie)
      var ca = decodedCookie.split(';')
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i]
        while (c.charAt(0) == ' ') {
          c = c.substring(1)
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length)
        }
      }
      return ''
    },

    getAddressObject(address_components) {
      var ShouldBeComponent = {
        street_number: ['street_number'],
        zip: ['postal_code'],
        address: ['street_address', 'route'],
        state: [
          'administrative_area_level_1',
          'administrative_area_level_2',
          'administrative_area_level_3',
          'administrative_area_level_4',
          'administrative_area_level_5'
        ],
        city: [
          'locality',
          'sublocality',
          'sublocality_level_1',
          'sublocality_level_2',
          'sublocality_level_3',
          'sublocality_level_4'
        ],
        country: ['country']
      }

      var address = {
        street_number: '',
        zip: '',
        address: '',
        state: '',
        city: '',
        country: ''
      }

      address_components.forEach(component => {
        for (var shouldBe in ShouldBeComponent) {
          if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
            if (shouldBe === 'country') {
              address[shouldBe] = component.short_name
            } else {
              address[shouldBe] = component.long_name
            }
          }
        }
      })

      return address
    },

    filterPlaces(item, queryText, itemText) {
      return true
    },

    selectPlace() {
      let that = this

      $.get(
        'https://maps.googleapis.com/maps/api/geocode/json?place_id=' +
          this.autocompleteLocationModel.id +
          '&fields=name,rating,formatted_phone_number&key=AIzaSyANqXQPtxZhmpNYyvoBPBrnatNxGMvN9Ks',
        function(response) {
          that.company_name = that.autocompleteLocationModel.value.split(',')[0]

          let address = that.getAddressObject(response.results[0].address_components)
          let business_location = {}
          business_location.street_number = address.street_number
          business_location.address = address.address
          business_location.city = address.city
          business_location.state = address.state
          business_location.zip = address.zip
          business_location.country = address.country

          business_location.lat = response.results[0].geometry.location.lat
          business_location.lng = response.results[0].geometry.location.lng
          business_location.place_id = response.results[0].place_id

          that.business_location = business_location
        }
      )
    }
  },

  mounted() {
    let self = this

    setTimeout(function() {
      if (self.$route.query.email) {
        self.$refs.email.value = self.$route.query.email
      }
    }, 500)

    if (self.$route.params.company_type) {
      if (self.$route.params.company_type == 'b') {
        self.company_type = 'business'
        self.only_business = true
      }
    }
  },

  watch: {
    locationSearchText(newVal) {
      var that = this

      if (!newVal || newVal.length <= 3) return

      GetSuggestions(newVal)
        .then(function(res) {
          that.locationEntries = res
        })
        .catch(function() {
          that.business = false
          that.step = 1
        })
    }
  }
}
</script>

<style scoped>
.v-stepper {
  border: 0px !important;
  box-shadow: none !important;
  padding: 0px !important;
}

.v-stepper__content {
  padding: 0px !important;
}
</style>